import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Send, Mic, Sparkles } from 'lucide-react';
import { PageTransition } from '@/components/motion/Animations';

const ARIA_AVATARS = {
  feminino: '/aria/feminino.png',
  hibrido: '/aria/hibrido.png',
  neutra: '/aria/hibrido.png',  // backward compat: 'neutra' uses same image
  masculino: '/aria/masculino.png',
};

const ARIA_OPTIONS = [
  { key: 'feminino', label: 'ARIA Feminina', desc: 'Acolhedora, suave e receptiva.' },
  { key: 'neutra', label: 'ARIA Neutra', desc: 'Inclusiva, equilibrada e universal.' },
  { key: 'masculino', label: 'ARIA Masculina', desc: 'Calma, segura e estável.' },
];

const QUICK_SUGGESTIONS = [
  'Como estou me sentindo hoje',
  'O que posso fazer na U-Clean?',
  'Preciso de ajuda agora',
];

export default function AriaPage() {
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] || 'você';

  const [ariaGender, setAriaGender] = useState(() => {
    const stored = localStorage.getItem('uclean_aria_gender') || '';
    // backward compat: treat 'hibrido' as 'neutra'
    if (stored === 'hibrido') return 'neutra';
    return stored;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [ariaState, setAriaState] = useState('idle');
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const bottomRef = useRef(null);

  // Load persisted history on mount (only when gender is set)
  useEffect(() => {
    if (!ariaGender || historyLoaded) return;
    (async () => {
      try {
        const res = await api.get('/aria/history');
        const history = res.data.messages || [];
        if (history.length > 0) {
          setMessages(history.map(m => ({
            role: m.role === 'assistant' ? 'aria' : 'user',
            content: m.content,
            ts: m.ts ? new Date(m.ts).getTime() : Date.now(),
          })));
          // Restore personality if saved server-side
          if (res.data.personality && res.data.personality !== ariaGender) {
            localStorage.setItem('uclean_aria_gender', res.data.personality);
          }
        } else {
          // No history — show welcome
          setMessages([
            { role: 'aria', content: `Olá, ${firstName}! Eu sou a ARIA, sua companheira terapêutica.`, ts: Date.now() },
            { role: 'aria', content: 'Estou aqui para te ouvir e apoiar. O que gostaria de compartilhar?', ts: Date.now() + 1 },
          ]);
        }
      } catch {
        // Fallback welcome on error
        setMessages([
          { role: 'aria', content: `Olá, ${firstName}! Eu sou a ARIA, sua companheira terapêutica.`, ts: Date.now() },
          { role: 'aria', content: 'Estou aqui para te ouvir e apoiar. O que gostaria de compartilhar?', ts: Date.now() + 1 },
        ]);
      }
      setHistoryLoaded(true);
    })();
  }, [ariaGender, historyLoaded, firstName]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, ariaState]);

  const handleGenderSelect = (gender) => {
    localStorage.setItem('uclean_aria_gender', gender);
    setAriaGender(gender);
    setHistoryLoaded(false); // trigger history load
  };

  const avatarSrc = ARIA_AVATARS[ariaGender] || ARIA_AVATARS.feminino;

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: text.trim(), ts: Date.now() }]);
    setInput('');
    setAriaState('thinking');

    try {
      const res = await api.post('/aria/chat', {
        message: text.trim(),
        personality: ariaGender,
        session_id: `aria_${user?.id || 'anon'}`,
      });
      setAriaState('speaking');
      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'aria',
          content: res.data.response,
          ts: Date.now(),
        }]);
        setAriaState('idle');
      }, 400);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'aria',
        content: 'Desculpe, houve um problema. Tente novamente.',
        ts: Date.now(),
      }]);
      setAriaState('idle');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  // Gender selection screen
  if (!ariaGender) {
    return (
      <PageTransition>
        <div className="max-w-2xl mx-auto py-12 px-4 text-center" data-testid="aria-gender-select">
          <h1 className="text-2xl font-bold mb-2">Como você prefere ser acolhido hoje?</h1>
          <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
            Escolha a presença da ARIA com a qual você sente mais conexão. Você pode mudar essa escolha quando quiser.
          </p>
          <div className="grid grid-cols-3 gap-4 mb-6">
            {ARIA_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => handleGenderSelect(opt.key)}
                className="flex flex-col items-center gap-3 p-4 rounded-2xl border border-border/40 hover:border-primary hover:shadow-lg transition-all group"
                data-testid={`aria-gender-${opt.key}`}
              >
                <div className="relative">
                  <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full overflow-hidden ring-4 ring-transparent group-hover:ring-primary/30 transition-all">
                    <img src={ARIA_AVATARS[opt.key]} alt={opt.label} className="w-full h-full object-cover" />
                  </div>
                </div>
                <span className="font-semibold text-sm">{opt.label}</span>
                <span className="text-[11px] text-muted-foreground leading-tight">{opt.desc}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground/60">Seu cuidado continua o mesmo em qualquer uma das opções.</p>
        </div>
      </PageTransition>
    );
  }

  const showSuggestions = messages.length <= 2 && !messages.some(m => m.role === 'user');

  return (
    <PageTransition>
      <div className="flex flex-col h-[calc(100vh-64px-80px)] md:h-[calc(100vh-64px-24px)] max-w-3xl mx-auto" data-testid="aria-page">

        {/* Header with avatar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/30">
          <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-violet-300 dark:ring-violet-700">
            <img src={avatarSrc} alt="ARIA" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-foreground flex items-center gap-1.5 text-sm">
              ARIA <Sparkles className="h-3 w-3 text-violet-500" />
            </h2>
            <p className="text-[11px] text-muted-foreground">
              {ariaState === 'thinking' ? 'Pensando...' : ariaState === 'speaking' ? 'Respondendo...' : 'Assistente terapêutica'}
            </p>
          </div>
          {/* Gender switch */}
          <button
            onClick={async () => {
              try { await api.delete('/aria/session'); } catch {}
              localStorage.removeItem('uclean_aria_gender');
              setAriaGender('');
              setMessages([]);
              setHistoryLoaded(false);
            }}
            className="text-[10px] text-muted-foreground hover:text-foreground px-2 py-1 rounded-lg hover:bg-muted transition-colors"
            data-testid="aria-change-gender"
          >
            Mudar
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="aria-messages">
          {/* Centered avatar with orbe aura on first load */}
          {showSuggestions && (
            <div className="flex flex-col items-center py-6">
              <AriaAvatarWithAura src={avatarSrc} state={ariaState} size="lg" />
              <p className="text-sm text-muted-foreground mt-4">Como posso te ajudar, {firstName}?</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              {msg.role === 'aria' ? (
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-violet-200 dark:ring-violet-800">
                  <img src={avatarSrc} alt="ARIA" className="w-full h-full object-cover" />
                </div>
              ) : (
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarFallback className="text-xs bg-primary/10 text-primary">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-tr-md'
                  : 'bg-muted text-foreground rounded-tl-md'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}

          {/* Thinking */}
          {ariaState === 'thinking' && (
            <div className="flex gap-2.5">
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-violet-200 dark:ring-violet-800">
                <img src={avatarSrc} alt="ARIA" className="w-full h-full object-cover" />
              </div>
              <div className="bg-muted rounded-2xl rounded-tl-md px-4 py-3">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-violet-400/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-violet-400/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-violet-400/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Quick Suggestions */}
        {showSuggestions && (
          <div className="px-4 pb-2 flex gap-2 flex-wrap" data-testid="aria-suggestions">
            {QUICK_SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                onClick={() => sendMessage(s)}
                className="text-xs px-3 py-1.5 rounded-full border border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/30 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors"
                data-testid={`suggestion-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 border-t border-border/30 bg-card/80 backdrop-blur-sm">
          <div className="flex gap-2 items-center">
            <Button variant="ghost" size="icon" className="rounded-xl h-10 w-10 flex-shrink-0 text-muted-foreground hover:text-violet-600" data-testid="aria-mic-btn">
              <Mic className="h-4 w-4" />
            </Button>
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite sua mensagem..."
              className="flex-1 rounded-xl border-border/50 bg-background"
              data-testid="aria-input"
            />
            <Button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || ariaState !== 'idle'}
              size="icon"
              className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white h-10 w-10"
              data-testid="aria-send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

/* ═══ Avatar with Animated Orbe Aura ═══ */
function AriaAvatarWithAura({ src, state = 'idle', size = 'lg' }) {
  const s = size === 'lg' ? 'w-32 h-32' : 'w-12 h-12';
  const ringSize = size === 'lg' ? 'w-40 h-40' : 'w-16 h-16';

  return (
    <div className={`relative ${ringSize} flex items-center justify-center`} data-testid="aria-avatar-aura">
      {/* Orbe aura behind avatar */}
      <div
        className={`absolute inset-0 rounded-full`}
        style={{
          background: state === 'thinking'
            ? 'radial-gradient(circle, rgba(167,139,250,0.35), rgba(99,102,241,0.15), transparent 70%)'
            : 'radial-gradient(circle, rgba(139,92,246,0.25), rgba(124,58,237,0.08), transparent 70%)',
          animation: state === 'thinking' ? 'auraPulseThink 1.2s ease-in-out infinite' : 'auraPulse 3s ease-in-out infinite',
        }}
      />
      {state === 'speaking' && (
        <>
          <div className="absolute inset-0 rounded-full bg-violet-400/10 animate-ping" style={{ animationDuration: '1.5s' }} />
          <div className="absolute inset-[-6px] rounded-full bg-violet-400/5 animate-ping" style={{ animationDuration: '2.2s', animationDelay: '0.3s' }} />
        </>
      )}

      {/* Avatar face */}
      <div className={`${s} rounded-full overflow-hidden ring-3 ring-violet-300/50 dark:ring-violet-700/50 relative z-10 shadow-lg`}>
        <img src={src} alt="ARIA" className="w-full h-full object-cover" loading="eager" />
      </div>

      <style>{`
        @keyframes auraPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.8; }
        }
        @keyframes auraPulseThink {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(0.95); opacity: 1.2; }
        }
      `}</style>
    </div>
  );
}
